import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Form,
  Select,
  Input,
  message,
  Tag,
  Divider,
  Button,
  Space,
} from "antd";
import {
  useCourierQuery,
  useCourierAssignMutation,
  useCourierAssignmentStatusUpdateMutation,
  useCourierUnassignMutation,
} from "../../Services";
import { Courier, CourierAssignmentStatus, Order } from "../../Types";

const { Option } = Select;

const ASSIGNMENT_STATUS_META: Record<
  CourierAssignmentStatus,
  { color: string }
> = {
  pending: { color: "orange" },
  picked_up: { color: "blue" },
  in_transit: { color: "purple" },
  delivered: { color: "green" },
  returned: { color: "red" },
  cancelled: { color: "default" },
};

export const ASSIGNMENT_STATUS_OPTIONS: CourierAssignmentStatus[] = [
  "pending",
  "picked_up",
  "in_transit",
  "delivered",
  "returned",
  "cancelled",
];

interface CourierAssignModalProps {
  open: boolean;
  order: Order | null;
  onClose: () => void;
  onChanged?: () => void;
}

export const CourierAssignModal: React.FC<CourierAssignModalProps> = ({
  open,
  order,
  onClose,
  onChanged,
}) => {
  const [form] = Form.useForm();
  const [currentStatus, setCurrentStatus] = useState<string>("");
  const [statusNote, setStatusNote] = useState<string>("");

  const { data: couriersResponse, refetch: refetchCouriers } =
    useCourierQuery();
  const assignMutation = useCourierAssignMutation();
  const statusMutation = useCourierAssignmentStatusUpdateMutation();
  const unassignMutation = useCourierUnassignMutation();

  const activeCouriers: Courier[] = useMemo(() => {
    const raw: Courier[] = Array.isArray(couriersResponse)
      ? couriersResponse
      : (couriersResponse as any)?.data || [];
    return raw.filter((c) => c.is_active === 1);
  }, [couriersResponse]);

  const assignment = order?.courier_assignment || null;

  useEffect(() => {
    if (open) {
      form.resetFields();
      setStatusNote("");
      setCurrentStatus(assignment?.status || "");
    }
  }, [open, order, assignment, form]);

  const handleClose = () => {
    onClose();
  };

  const handleAssign = async () => {
    if (!order) return;
    try {
      const values = await form.validateFields();
      const res = await assignMutation.mutateAsync({
        order_id: order.id,
        courier_id: values.courier_id,
        tracking_no: values.tracking_no || undefined,
        status: values.status || "pending",
        notes: values.notes || undefined,
      });
      if (res?.success) {
        message.success(res?.message || "Courier assigned successfully.");
        handleClose();
        onChanged?.();
      } else {
        message.error(res?.message || "Failed to assign courier.");
      }
    } catch (err: any) {
      message.error(err?.message || "Please fill all required fields.");
    }
  };

  const handleUpdateStatus = async () => {
    if (!assignment) return;
    try {
      const res = await statusMutation.mutateAsync({
        assignment_id: assignment.id,
        status: currentStatus,
        notes: statusNote || undefined,
      });
      if (res?.success) {
        message.success(res?.message || "Courier status updated.");
        onChanged?.();
      } else {
        message.error(res?.message || "Failed to update courier status.");
      }
    } catch (err: any) {
      message.error(err?.message || "Failed to update courier status.");
    }
  };

  const handleUnassign = async () => {
    if (!assignment) return;
    try {
      const res = await unassignMutation.mutateAsync(assignment.id);
      if (res?.success) {
        message.success(res?.message || "Courier unassigned.");
        handleClose();
        onChanged?.();
      } else {
        message.error(res?.message || "Failed to unassign courier.");
      }
    } catch (err: any) {
      message.error(err?.message || "Failed to unassign courier.");
    }
  };

  const isBusy =
    assignMutation.isPending ||
    statusMutation.isPending ||
    unassignMutation.isPending;

  return (
    <Modal
      title={
        order
          ? `Courier Assignment - ${order.order_no || ""}`
          : "Courier Assignment"
      }
      open={open}
      onCancel={handleClose}
      footer={
        assignment
          ? [
              <Button
                key="unassign"
                danger
                onClick={handleUnassign}
                loading={unassignMutation.isPending}
                disabled={isBusy && !unassignMutation.isPending}
              >
                Unassign
              </Button>,
              <Button key="cancel" onClick={handleClose}>
                Close
              </Button>,
              <Button
                key="update"
                type="primary"
                onClick={handleUpdateStatus}
                loading={statusMutation.isPending}
                disabled={isBusy && !statusMutation.isPending}
              >
                Update Status
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={handleClose}>
                Cancel
              </Button>,
              <Button
                key="assign"
                type="primary"
                onClick={handleAssign}
                loading={assignMutation.isPending}
                disabled={isBusy && !assignMutation.isPending}
              >
                Assign Courier
              </Button>,
            ]
      }
    >
      {assignment ? (
        <div>
          <div className="mb-3 p-3 rounded bg-gray-50 border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-800">
                {assignment.courier_name || "Courier"}
              </span>
              <Tag color={ASSIGNMENT_STATUS_META[assignment.status]?.color}>
                {assignment.status}
              </Tag>
            </div>
            {assignment.courier_phone && (
              <div className="text-xs text-gray-500 mt-1">
                Phone: {assignment.courier_phone}
              </div>
            )}
            {assignment.tracking_no && (
              <div className="text-xs text-gray-500 mt-0.5">
                Tracking No: {assignment.tracking_no}
              </div>
            )}
          </div>

          <Form layout="vertical">
            <Form.Item label="Update Status" required>
              <Select
                value={currentStatus}
                onChange={setCurrentStatus}
                placeholder="Select courier status"
              >
                {ASSIGNMENT_STATUS_OPTIONS.map((s) => (
                  <Option key={s} value={s}>
                    <Tag
                      color={ASSIGNMENT_STATUS_META[s].color}
                      className="m-0 capitalize"
                    >
                      {s}
                    </Tag>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Status Note (Optional)">
              <Input.TextArea
                rows={2}
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                placeholder="Add a note about this status change"
                style={{ resize: "none" }}
              />
            </Form.Item>
          </Form>
        </div>
      ) : (
        <>
          <Divider style={{ margin: "0 0 16px" }} />
          <Form form={form} layout="vertical">
            <Form.Item
              label="Courier"
              name="courier_id"
              rules={[{ required: true, message: "Please select a courier." }]}
            >
              <Select
                placeholder="Select courier"
                showSearch
                optionFilterProp="children"
                onDropdownVisibleChange={(visible) => {
                  if (visible) refetchCouriers();
                }}
              >
                {activeCouriers.map((c) => (
                  <Option key={c.id} value={c.id}>
                    {c.name}
                    {c.phone ? ` - ${c.phone}` : ""}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Tracking No (Optional)" name="tracking_no">
              <Input placeholder="Enter courier tracking number" />
            </Form.Item>
            <Form.Item
              label="Initial Status"
              name="status"
              initialValue="pending"
            >
              <Select placeholder="Select initial status">
                {ASSIGNMENT_STATUS_OPTIONS.map((s) => (
                  <Option key={s} value={s}>
                    <Tag
                      color={ASSIGNMENT_STATUS_META[s].color}
                      className="m-0 capitalize"
                    >
                      {s}
                    </Tag>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Notes (Optional)" name="notes">
              <Input.TextArea
                rows={2}
                placeholder="Any delivery instructions"
                style={{ resize: "none" }}
              />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
};
